import { z } from "zod";

export const CONNECTION_PROVIDER_IDS = [
  "pipedream",
  "dynamics_business_central",
  "hubspot",
  "salesforce",
  "epicor",
  "prophet21",
  "jobboss2",
  "odyssey",
  "slack",
  "eniteo",
  "fake_erp",
] as const;

export const ConnectionProviderKeySchema = z.enum(CONNECTION_PROVIDER_IDS);

export type ConnectionProviderKey = (typeof CONNECTION_PROVIDER_IDS)[number];

export const DYNAMICS_PROVIDER_ID: ConnectionProviderKey =
  "dynamics_business_central";
export const HUBSPOT_PROVIDER_ID: ConnectionProviderKey = "hubspot";
export const SALESFORCE_PROVIDER_ID: ConnectionProviderKey = "salesforce";
export const EPICOR_PROVIDER_ID: ConnectionProviderKey = "epicor";
export const PROPHET21_PROVIDER_ID: ConnectionProviderKey = "prophet21";
export const JOBBOSS2_PROVIDER_ID: ConnectionProviderKey = "jobboss2";
export const ODYSSEY_PROVIDER_ID: ConnectionProviderKey = "odyssey";
export const ENITEO_PROVIDER_ID: ConnectionProviderKey = "eniteo";
export const PIPEDREAM_PROVIDER_ID: ConnectionProviderKey = "pipedream";

export const DYNAMICS_BUSINESS_CENTRAL_APP_ID = "dynamics_business_central";
export const HUBSPOT_APP_ID = "hubspot";
export const SALESFORCE_APP_ID = "salesforce";
export const EPICOR_APP_ID = "epicor";
export const PROPHET21_APP_ID = "prophet21";
export const JOBBOSS2_APP_ID = "jobboss2";
export const JOBBOSS2_DISPLAY_NAME = "JobBOSS²";
export const ODYSSEY_APP_ID = "odyssey";
export const ODYSSEY_DISPLAY_NAME = "Odyssey";
export const ENITEO_APP_ID = "eniteo";
export const SLACK_APP_ID = "slack";

/**
 * Minimal shared shape for any stored OAuth credential: a discriminant `type`
 * and the bearer `accessToken` used to authenticate upstream calls. Providers
 * extend this with whatever else they need (refresh fields, team identity, …).
 */
export const OAuthStoredCredentialSchema = z.object({
  type: z.string(),
  accessToken: z.string(),
});

export type OAuthStoredCredential = z.infer<typeof OAuthStoredCredentialSchema>;

export const SlackCredentialsSchema = OAuthStoredCredentialSchema.extend({
  type: z.literal("SlackCredentials"),
  teamId: z.string(),
  teamName: z.string().optional(),
  botUserId: z.string().optional(),
});

export type SlackCredentials = z.infer<typeof SlackCredentialsSchema>;

/** Configuration for OAuth-based connection providers used by the frontend callback page. */
export interface OAuthProviderConfig {
  displayName: string;
  connectionProviderId: ConnectionProviderKey;
  appId: string;
}

/**
 * Registry of OAuth providers that use the redirect-based authorization flow.
 * Keyed by the URL path segment used in `/auth/:provider/callback`.
 */
export const OAUTH_PROVIDERS: Record<string, OAuthProviderConfig> = {
  hubspot: {
    displayName: "HubSpot",
    connectionProviderId: "hubspot",
    appId: HUBSPOT_APP_ID,
  },
  salesforce: {
    displayName: "Salesforce",
    connectionProviderId: "salesforce",
    appId: SALESFORCE_APP_ID,
  },
  slack: {
    displayName: "Slack",
    connectionProviderId: "slack",
    appId: SLACK_APP_ID,
  },
};

export const DynamicsBusinessCentralCredentialsSchema = z.object({
  type: z.literal("DynamicsBusinessCentralCredentials"),
  accessToken: z.string(),
  refreshToken: z.string(),
  expiresAt: z.string(),
  scope: z.string().optional(),
  tokenType: z.string().optional(),
});

export type DynamicsBusinessCentralCredentials = z.infer<
  typeof DynamicsBusinessCentralCredentialsSchema
>;

export const DynamicsBusinessCentralConfigSchema = z
  .object({
    tenantId: z.string().optional(),
    environmentName: z.string().optional(),
    companyId: z.string().optional(),
    companyDisplayName: z.string().optional(),
    apiVersion: z.string().optional(),
  })
  .strict();

export type DynamicsBusinessCentralConfig = z.infer<
  typeof DynamicsBusinessCentralConfigSchema
>;

export const DynamicsBusinessCentralServiceCredentialsSchema = z.object({
  type: z.literal("DynamicsBusinessCentralServiceCredentials"),
  tenantId: z.string(),
});

export type DynamicsBusinessCentralServiceCredentials = z.infer<
  typeof DynamicsBusinessCentralServiceCredentialsSchema
>;

export const EpicorStoredCredentialsSchema = z
  .object({
    type: z.literal("EpicorServiceCredentials"),
    username: z.string().trim().min(1),
    password: z.string().min(1),
    apiKey: z.string().trim().min(1).optional(),
  })
  .strict();

export type EpicorStoredCredentials = z.infer<
  typeof EpicorStoredCredentialsSchema
>;

export const EpicorConnectionConfigSchema = z
  .object({
    baseUrl: z.string().url(),
  })
  .strict();

export type EpicorConnectionConfig = z.infer<
  typeof EpicorConnectionConfigSchema
>;

/**
 * Client-credentials pair issued by ECI for one JobBOSS² company. Both values
 * are stored verbatim: whitespace can be part of an externally issued
 * credential, so trimming would break authentication.
 */
export const JobBoss2StoredCredentialsSchema = z
  .object({
    type: z.literal("JobBoss2ServiceCredentials"),
    clientId: z.string().min(1),
    clientSecret: z.string().min(1),
  })
  .strict();

/** Client-credentials pair issued by ECI for one JobBOSS² company. */
export type JobBoss2StoredCredentials = z.infer<
  typeof JobBoss2StoredCredentialsSchema
>;

/** JobBOSS² company discovered at finalize time; every field is nullable upstream. */
export const JobBoss2ConnectionConfigSchema = z
  .object({
    companyCode: z.string(),
    companyDescription: z.string().nullable(),
    currencyCode: z.string().nullable(),
    companyTimeZone: z.string().nullable(),
  })
  .strict();

/** JobBOSS² company discovered at finalize time; every field is nullable upstream. */
export type JobBoss2ConnectionConfig = z.infer<
  typeof JobBoss2ConnectionConfigSchema
>;

/** Persistent Odyssey API key issued in the Odyssey UI, stored verbatim. */
export const OdysseyStoredCredentialsSchema = z
  .object({
    type: z.literal("OdysseyServiceCredentials"),
    apiKey: z.string().min(1),
  })
  .strict();

/** Persistent Odyssey API key issued in the Odyssey UI, stored verbatim. */
export type OdysseyStoredCredentials = z.infer<
  typeof OdysseyStoredCredentialsSchema
>;

/** Odyssey API host and the plant (`CompanyID`) one connection syncs. */
export const OdysseyConnectionConfigSchema = z
  .object({
    baseUrl: z.string().url(),
    companyId: z.string().min(1),
  })
  .strict();

/** Odyssey API host and the plant (`CompanyID`) one connection syncs. */
export type OdysseyConnectionConfig = z.infer<
  typeof OdysseyConnectionConfigSchema
>;

/**
 * Stored Prophet 21 credentials, discriminated on `authMode` because Prophet 21
 * accepts either a user's username and password or a middleware consumer key.
 */
export const Prophet21StoredCredentialsSchema = z.discriminatedUnion("authMode", [
  z
    .object({
      type: z.literal("Prophet21ServiceCredentials"),
      authMode: z.literal("password"),
      username: z.string().min(1),
      password: z.string().min(1),
    })
    .strict(),
  z
    .object({
      type: z.literal("Prophet21ServiceCredentials"),
      authMode: z.literal("consumerKey"),
      consumerKey: z.string().min(1),
      username: z.string().min(1).optional(),
    })
    .strict(),
]);

/** Stored Prophet 21 credentials. */
export type Prophet21StoredCredentials = z.infer<
  typeof Prophet21StoredCredentialsSchema
>;

/**
 * Prophet 21 connection config as submitted by the setup dialog, before the
 * connection provider resolves the UI-server URL from the middleware router.
 */
export const Prophet21ConnectionSetupConfigSchema = z
  .object({
    baseUrl: z.string().url(),
    companyId: z.string().min(1),
  })
  .strict();

/** Prophet 21 connection config as submitted by the setup dialog. */
export type Prophet21ConnectionSetupConfig = z.infer<
  typeof Prophet21ConnectionSetupConfigSchema
>;

/** Prophet 21 connection config as stored on a finalized connection. */
export const Prophet21ConnectionConfigSchema =
  Prophet21ConnectionSetupConfigSchema.extend({
    uiServerUrl: z.string().url(),
  });

/** Prophet 21 connection config as stored on a finalized connection. */
export type Prophet21ConnectionConfig = z.infer<
  typeof Prophet21ConnectionConfigSchema
>;


/**
 * Eniteo SQL Server service-account credentials, stored in the connection's
 * private credential payload. Carries an optional separate login for quote
 * pushes.
 */
export const EniteoStoredCredentialsSchema = z
  .object({
    type: z.literal("EniteoServiceCredentials"),
    username: z.string().trim().min(1),
    password: z.string().min(1),
    write: z
      .object({
        username: z.string().trim().min(1),
        password: z.string().min(1),
      })
      .strict()
      .optional(),
  })
  .strict();

/** Eniteo SQL Server service-account credentials. */
export type EniteoStoredCredentials = z.infer<
  typeof EniteoStoredCredentialsSchema
>;

const EniteoEndpointFields = {
  server: z.string().trim().min(1),
  port: z.number().int().min(1).max(65535).default(1433),
  database: z.string().trim().min(1),
};

/**
 * A separate Eniteo SQL Server database that quote pushes write to, for
 * customers who keep reads on a replica. Signs in with the stored write login
 * when one exists, else with the connection's login.
 */
export const EniteoWriteEndpointSchema = z.preprocess(
  splitEniteoServerAddress,
  z.object(EniteoEndpointFields).strict(),
);

/** A separate Eniteo SQL Server database that quote pushes write to. */
export type EniteoWriteEndpoint = z.infer<typeof EniteoWriteEndpointSchema>;

/**
 * Eniteo SQL Server endpoint, stored on the connection's public config column.
 * Carries no `instanceName`: resolving a named instance needs UDP 1434, which
 * the static egress CONNECT tunnel cannot carry, so the fixed TCP port is
 * always given explicitly. Pulls read `server`/`database`; quote pushes write
 * to `write` when set, else to the same database.
 */
export const EniteoConnectionConfigSchema = z.preprocess(
  splitEniteoServerAddress,
  z
    .object({
      ...EniteoEndpointFields,
      encrypt: z.boolean().default(true),
      trustServerCertificate: z.boolean().default(false),
      write: EniteoWriteEndpointSchema.optional(),
    })
    .strict(),
);

const ENITEO_SERVER_ADDRESS =
  /^\s*(?:[a-z][a-z0-9+.-]*:\/\/)?([^\s/:,]+)\s*(?:[:,]\s*(\d+))?\s*\/?\s*$/i;

/**
 * Accepts the server as it is usually handed over - a URL like
 * `http://connect.enmark.com: 51572` or a connection string like
 * `connect.enmark.com,51572` - by dropping the scheme and moving an embedded
 * port into `port`.
 * @param input - The raw config value
 * @returns The config with a bare hostname, or the input untouched when it does not match
 */
function splitEniteoServerAddress(input: unknown): unknown {
  if (typeof input !== "object" || input === null || !("server" in input)) {
    return input;
  }
  const match =
    typeof input.server === "string"
      ? ENITEO_SERVER_ADDRESS.exec(input.server)
      : null;
  if (!match) {
    return input;
  }
  const [, host, port] = match;
  return port
    ? { ...input, server: host, port: Number(port) }
    : { ...input, server: host };
}

/** Eniteo SQL Server endpoint configuration. */
export type EniteoConnectionConfig = z.infer<
  typeof EniteoConnectionConfigSchema
>;

/**
 * Base schema for refreshing OAuth providers (HubSpot, Salesforce, etc.):
 * extends the minimal stored-credential shape with the refresh token, expiry,
 * and granted scopes. Providers extend this with any further fields they need.
 */
export const OAuthCredentialsBaseSchema = OAuthStoredCredentialSchema.extend({
  refreshToken: z.string(),
  expiresAt: z.string(),
  grantedScopes: z.array(z.string()).optional(),
});

export type OAuthCredentialsBase = z.infer<typeof OAuthCredentialsBaseSchema>;

export const HubSpotCredentialsSchema = OAuthCredentialsBaseSchema.extend({
  type: z.literal("HubSpotCredentials"),
});

export type HubSpotCredentials = z.infer<typeof HubSpotCredentialsSchema>;

export const SalesforceCredentialsSchema = OAuthCredentialsBaseSchema.extend({
  type: z.literal("SalesforceCredentials"),
});

export type SalesforceCredentials = z.infer<typeof SalesforceCredentialsSchema>;

export const SalesforceConfigSchema = z.object({
  instanceUrl: z.string(),
});

export type SalesforceConfig = z.infer<typeof SalesforceConfigSchema>;

export const ConnectionConfigSchema = z.union([
  DynamicsBusinessCentralConfigSchema,
  SalesforceConfigSchema,
  EpicorConnectionConfigSchema,
  Prophet21ConnectionConfigSchema,
  Prophet21ConnectionSetupConfigSchema,
  JobBoss2ConnectionConfigSchema,
  OdysseyConnectionConfigSchema,
  EniteoConnectionConfigSchema,
]);
export type ConnectionConfig = z.infer<typeof ConnectionConfigSchema>;

export const ConnectionCredentialSchema = z
  .union([
    DynamicsBusinessCentralCredentialsSchema,
    DynamicsBusinessCentralServiceCredentialsSchema,
    HubSpotCredentialsSchema,
    SalesforceCredentialsSchema,
    EpicorStoredCredentialsSchema,
    Prophet21StoredCredentialsSchema,
    JobBoss2StoredCredentialsSchema,
    OdysseyStoredCredentialsSchema,
    EniteoStoredCredentialsSchema,
    SlackCredentialsSchema,
    z.record(z.string(), z.unknown()),
  ])
  .nullable();

export type ConnectionCredential = z.infer<typeof ConnectionCredentialSchema>;

export const ConnectionStatusEnum = z.enum(["CONNECTED", "ERROR", "PENDING"]);

export type ConnectionStatus = z.infer<typeof ConnectionStatusEnum>;

export const ConnectionSchema = z.object({
  id: z.string(),
  externalConnectionId: z.string().nullable(),
  externalAccountFingerprint: z.string().nullable(),
  appId: z.string(),
  name: z.string().nullable(),
  userId: z.string(),
  organizationId: z.string(),
  connectionProviderId: ConnectionProviderKeySchema,
  status: ConnectionStatusEnum,
  credentials: ConnectionCredentialSchema,
  config: ConnectionConfigSchema.nullable(),
  requiresEgressProxy: z.boolean(),
  expiresAt: z.coerce.date().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type Connection = z.infer<typeof ConnectionSchema>;

export const PublicConnectionSchema = ConnectionSchema.omit({
  credentials: true,
});

export type PublicConnection = z.infer<typeof PublicConnectionSchema>;

export const ConnectionsResponseSchema = z.object({
  connections: z.array(PublicConnectionSchema),
});

export type ConnectionsResponse = z.infer<typeof ConnectionsResponseSchema>;

export const ConnectionResponseSchema = z.object({
  connection: PublicConnectionSchema,
});

export type ConnectionResponse = z.infer<typeof ConnectionResponseSchema>;

/**
 * Standard OAuth2 token response fields shared across providers.
 * Uses `.passthrough()` to preserve any extra provider-specific fields.
 */
export const OAuthTokenResponseSchema = z
  .object({
    access_token: z.string(),
    refresh_token: z.string(),
    expires_in: z.number(),
  })
  .passthrough();

export type OAuthTokenResponse = z.infer<typeof OAuthTokenResponseSchema>;

export const ConnectionTokenResponseSchema = z.object({
  token: z.string().optional(),
  clientId: z.string().optional(),
  authorizationUrl: z.string().optional(),
  state: z.string().optional(),
  expiresAt: z.string().optional(),
  additionalParams: z.record(z.string(), z.unknown()).optional(),
});

export type ConnectionTokenResponse = z.infer<
  typeof ConnectionTokenResponseSchema
>;
